import React from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { StudioCommand } from '../../hooks/useCommandPalette'

export interface StudioCommandPaletteProps {
  isOpen: boolean
  query: string
  commands: StudioCommand[]
  onQueryChange: (q: string) => void
  onRun: (command: StudioCommand) => void
  onClose: () => void
}

const Veil = styled(Box, { shouldForwardProp: (p) => p !== '$open' })<{ $open?: boolean }>(({ $open }) => ({
  position: 'fixed',
  inset: 0,
  zIndex: 90,
  background: 'rgba(6,5,10,0.55)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  display: $open ? 'flex' : 'none',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingTop: '14vh',
}))

const Palette = styled(Box)({
  width: 480,
  borderRadius: 16,
  overflow: 'hidden',
  background: 'rgba(23,22,29,0.97)',
  border: '1px solid rgba(244,243,246,0.14)',
  boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
})

const Input = styled('input')({
  width: '100%',
  height: 48,
  padding: '0 18px',
  boxSizing: 'border-box',
  background: 'none',
  border: 'none',
  outline: 'none',
  color: 'var(--ds-night-ink)',
  fontFamily: 'var(--ds-font)',
  fontSize: 14,
  borderBottom: '1px solid var(--ds-night-border)',
})

const List = styled('ul')({ listStyle: 'none', margin: 0, maxHeight: 300, overflowY: 'auto', padding: 6 })

const Item = styled('li', { shouldForwardProp: (p) => p !== '$hot' })<{ $hot?: boolean }>(({ $hot }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 11,
  height: 38,
  padding: '0 12px',
  borderRadius: 9,
  fontSize: 13,
  cursor: 'pointer',
  color: $hot ? 'var(--ds-night-ink)' : 'var(--ds-night-ink-muted)',
  background: $hot ? 'rgba(190,157,253,0.13)' : 'transparent',
  '&:hover': { background: 'rgba(190,157,253,0.13)', color: 'var(--ds-night-ink)' },
}))

const ItemIcon = styled('span')({ width: 17, textAlign: 'center', opacity: 0.8 })
const ItemShortcut = styled('span')({
  marginLeft: 'auto',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  color: 'var(--ds-night-ink-subtle)',
  border: '1px solid var(--ds-night-border)',
  borderRadius: 5,
  padding: '2px 6px',
})

/** ⌘K palette — search-filtered actions across the whole studio. */
export function StudioCommandPalette({ isOpen, query, commands, onQueryChange, onRun, onClose }: StudioCommandPaletteProps) {
  return (
    <Veil $open={isOpen} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <Palette onClick={(e) => e.stopPropagation()}>
        <Input
          autoFocus
          placeholder='Search rooms, prints, actions…'
          spellCheck={false}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && commands[0]) onRun(commands[0])
            if (e.key === 'Escape') onClose()
          }}
        />
        <List>
          {commands.map((c, i) => (
            <Item key={c.id} $hot={i === 0} onClick={() => onRun(c)}>
              <ItemIcon>{c.icon}</ItemIcon>
              <span>{c.label}</span>
              {c.shortcut && <ItemShortcut>{c.shortcut}</ItemShortcut>}
            </Item>
          ))}
        </List>
      </Palette>
    </Veil>
  )
}
